import { motion } from "framer-motion";

// Floating cards live in the LEFT/RIGHT wings only, never behind the hero text.
// Visible at xl breakpoint (>=1280px) where there is enough lateral space.
const tiles = [
  {
    type: "code",
    lang: "ts",
    top: "16%",
    left: "1.5%",
    width: 240,
    delay: 0,
    lines: [
      { t: "// liskov.agent.ts", c: "muted" },
      { t: "exploreRoute(/checkout)", c: "fg" },
      { t: "→ click('Add to cart')", c: "accent" },
      { t: "→ throttle(3G)", c: "muted" },
      { t: "→ retry  ✓ 3 paths", c: "fg" },
    ],
  },
  {
    type: "browser",
    top: "12%",
    right: "1.5%",
    width: 260,
    delay: 0.25,
    label: "preview · 1440 × 900",
  },
  {
    type: "api",
    top: "56%",
    left: "1.5%",
    width: 220,
    delay: 0.55,
    method: "POST",
    path: "/api/orders",
    status: 201,
    ms: 84,
  },
  {
    type: "reason",
    top: "54%",
    right: "1.5%",
    width: 250,
    delay: 0.85,
  },
];

export default function HeroBackground() {
  return (
    <div
      data-testid="hero-background"
      aria-hidden
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      {/* Grid */}
      <div className="absolute inset-0 bg-grid radial-fade" />

      {/* Accent radial glow */}
      <div
        className="absolute left-1/2 top-[20%] -translate-x-1/2 w-[920px] h-[420px] rounded-full"
        style={{
          background:
            "radial-gradient(ellipse at center, hsl(var(--accent) / 0.18), transparent 60%)",
          filter: "blur(40px)",
        }}
      />

      {/* Vertical accent line in center */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-full w-px bg-gradient-to-b from-transparent via-foreground/[0.06] to-transparent" />

      {/* Floating tiles — only on extra-large screens where the side wings can hold them */}
      <div className="hidden xl:block opacity-90">
        {tiles.map((tile, i) => (
          <FloatTile key={i} tile={tile} index={i} />
        ))}
      </div>

      {/* Bottom fade */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
}

function FloatTile({ tile, index }) {
  const style = {
    top: tile.top,
    left: tile.left,
    right: tile.right,
    bottom: tile.bottom,
    width: tile.width,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{
        opacity: 1,
        y: [0, -10, 0],
      }}
      transition={{
        opacity: { delay: tile.delay, duration: 0.8 },
        y: {
          delay: tile.delay,
          duration: 7 + index * 0.5,
          repeat: Infinity,
          ease: "easeInOut",
        },
      }}
      style={style}
      className="absolute float-card backdrop-blur-md"
    >
      {tile.type === "code" && <CodeTile tile={tile} />}
      {tile.type === "browser" && <BrowserTile tile={tile} />}
      {tile.type === "api" && <ApiTile tile={tile} />}
      {tile.type === "reason" && <ReasonTile />}
    </motion.div>
  );
}

function WindowChrome({ label }) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border">
      <span className="h-2 w-2 rounded-full bg-foreground/15" />
      <span className="h-2 w-2 rounded-full bg-foreground/15" />
      <span className="h-2 w-2 rounded-full bg-foreground/15" />
      {label && (
        <span className="ml-auto text-[10px] font-mono text-muted-foreground">
          {label}
        </span>
      )}
    </div>
  );
}

function CodeTile({ tile }) {
  return (
    <div>
      <WindowChrome label={tile.lang} />
      <div className="px-4 py-3 font-mono text-[11px] leading-relaxed">
        {tile.lines.map((l, i) => (
          <div
            key={i}
            className={
              l.c === "accent"
                ? "text-[hsl(var(--accent))]"
                : l.c === "muted"
                ? "text-muted-foreground"
                : "text-foreground/85"
            }
          >
            {l.t}
          </div>
        ))}
      </div>
    </div>
  );
}

function BrowserTile({ tile }) {
  return (
    <div>
      <WindowChrome label={tile.label} />
      <div className="px-3 py-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="accent-dot animate-pulse-glow" />
          <span className="text-[11px] font-mono text-muted-foreground">
            visiting / settings
          </span>
        </div>
        <div className="space-y-1.5">
          <div className="h-1.5 rounded-full bg-foreground/10 w-[60%]" />
          <div className="h-1.5 rounded-full bg-foreground/10 w-[80%]" />
          <div className="h-1.5 rounded-full bg-foreground/10 w-[40%]" />
        </div>
        <div className="grid grid-cols-3 gap-1.5 pt-2">
          <div className="h-8 rounded bg-foreground/[0.06]" />
          <div className="h-8 rounded bg-foreground/[0.06]" />
          <div className="h-8 rounded bg-[hsl(var(--accent)/0.15)] border border-[hsl(var(--accent)/0.4)]" />
        </div>
      </div>
    </div>
  );
}

function ApiTile({ tile }) {
  return (
    <div className="px-4 py-3 font-mono text-[11px]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          request
        </span>
        <span className="text-[10px] text-[hsl(var(--accent))]">{tile.ms}ms</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))]">
          {tile.method}
        </span>
        <span className="text-foreground/85">{tile.path}</span>
      </div>
      <div className="mt-2 flex items-center gap-2 text-muted-foreground">
        <span>status</span>
        <span className="text-foreground/85">{tile.status}</span>
        <span className="text-foreground/30">·</span>
        <span>contract ✓</span>
      </div>
    </div>
  );
}

function ReasonTile() {
  return (
    <div className="px-4 py-3 text-[11.5px]">
      <div className="flex items-center gap-2 mb-2">
        <span className="accent-dot" />
        <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
          reasoning
        </span>
      </div>
      <p className="text-foreground/85 leading-relaxed">
        New <span className="font-mono text-[hsl(var(--accent))]">/checkout</span>{" "}
        changes detected. Predicting impact on guest flow & mobile breakpoint.
      </p>
      <div className="mt-2 grid grid-cols-3 gap-1">
        <Pill label="guest" />
        <Pill label="coupons" />
        <Pill label="mobile" />
      </div>
    </div>
  );
}

function Pill({ label }) {
  return (
    <span className="text-[10px] font-mono text-center px-2 py-1 rounded-full border border-border text-muted-foreground">
      {label}
    </span>
  );
}
